import { useState, useRef, useEffect } from "react";
import { ScanLine, RotateCcw, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useSkuLocations, useMoveSkuLocations } from "@/hooks/use-warehouse";
import { WarehouseNav } from "@/components/warehouse/warehouse-nav";
import { playSuccess, playError } from "@/lib/sounds";
import { normalizeScannedBinCode } from "@/lib/warehouse/bin-label";
import { cn } from "@/lib/utils";
import { isolateScannerKeys } from "@/hooks/use-scanner-input";

type PageState = "scan-sku" | "select-locations" | "scan-destination";

export function MovePage() {
  const [state, setState] = useState<PageState>("scan-sku");
  const [skuInput, setSkuInput] = useState("");
  const [activeSku, setActiveSku] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [destInput, setDestInput] = useState("");

  const skuInputRef = useRef<HTMLInputElement>(null);
  const destInputRef = useRef<HTMLInputElement>(null);

  const { data: locations } = useSkuLocations(activeSku);
  const moveMutation = useMoveSkuLocations();

  useEffect(() => {
    skuInputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (state === "scan-destination") {
      setTimeout(() => destInputRef.current?.focus(), 100);
    }
  }, [state]);

  function reset() {
    setState("scan-sku");
    setSkuInput("");
    setActiveSku(null);
    setSelectedIds([]);
    setDestInput("");
    setTimeout(() => skuInputRef.current?.focus(), 100);
  }

  function handleSkuSubmit(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key !== "Enter") return;
    e.preventDefault();

    const nextSku = e.currentTarget.value.trim();
    if (!nextSku) return;

    setActiveSku(nextSku);
    setSkuInput("");
    setState("select-locations");
  }

  useEffect(() => {
    if (state === "select-locations" && locations && locations.length === 0) {
      playError();
      toast.error("SKU nije pronađen");
      reset();
    }
  }, [locations, state]);

  function toggleLocation(id: string) {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }

  function handleTransferClick() {
    if (selectedIds.length === 0) return;
    setState("scan-destination");
  }

  function handleDestSubmit(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key !== "Enter") return;
    e.preventDefault();

    const nextDestination = normalizeScannedBinCode(e.currentTarget.value);
    if (!nextDestination) return;

    setDestInput(nextDestination);
    moveMutation.mutate(
      { locationIds: selectedIds, destinationBinCode: nextDestination },
      {
        onSuccess: (data) => {
          playSuccess();
          toast.success(`Premješteno ${data.moved} lokacije na ${data.destinationBin.label}`);
          reset();
        },
        onError: (err: any) => {
          playError();
          toast.error(err?.message || "Greška pri premještanju");
          setDestInput("");
        },
      }
    );
  }

  return (
    <div className="flex flex-col h-full">
      <WarehouseNav />
      <div className="p-4 border-b flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-medium">Premještanje</h2>
          {state !== "scan-sku" && (
            <Button variant="ghost" size="icon-xs" onClick={reset}>
              <RotateCcw className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>

        {state === "scan-sku" && (
          <div className="relative">
            <ScanLine className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              ref={skuInputRef}
              value={skuInput}
              onChange={(e) => setSkuInput(e.target.value)}
              onKeyDown={isolateScannerKeys(handleSkuSubmit)}
              placeholder="Skeniraj SKU"
              className="h-11 pl-9 text-sm"
              autoComplete="off"
            />
          </div>
        )}

        {state === "scan-destination" && (
          <div className="relative">
            <ScanLine className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              ref={destInputRef}
              value={destInput}
              onChange={(e) => setDestInput(e.target.value)}
              onKeyDown={isolateScannerKeys(handleDestSubmit)}
              placeholder="Skeniraj novu lokaciju"
              className="h-11 pl-9 text-sm"
              autoComplete="off"
            />
          </div>
        )}
      </div>

      {state === "select-locations" && locations && locations.length > 0 && (
        <div className="flex-1 overflow-auto">
          <div className="px-4 py-3 border-b bg-muted/30">
            <span className="text-sm font-medium">{activeSku}</span>
            <span className="text-xs text-muted-foreground ml-2">
              {locations.length} lokacija
            </span>
          </div>

          <div className="flex flex-col">
            {locations.map((loc: any) => (
              <button
                key={loc.id}
                onClick={() => toggleLocation(loc.id)}
                className={cn(
                  "flex items-center gap-3 px-4 py-3 border-b text-left transition-colors",
                  selectedIds.includes(loc.id) && "bg-primary/5"
                )}
              >
                <div
                  className={cn(
                    "h-4 w-4 border rounded-sm shrink-0 flex items-center justify-center",
                    selectedIds.includes(loc.id)
                      ? "bg-primary border-primary"
                      : "border-muted-foreground/30"
                  )}
                >
                  {selectedIds.includes(loc.id) && (
                    <Check className="h-3 w-3 text-primary-foreground" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium tabular-nums">{loc.binLabel}</span>
                  <span className="text-xs text-muted-foreground ml-2">{loc.warehouseName}</span>
                </div>
              </button>
            ))}
          </div>

          <div className="p-4 border-t">
            <Button
              onClick={handleTransferClick}
              disabled={selectedIds.length === 0}
              className="w-full h-11"
            >
              <ArrowRight className="h-4 w-4 mr-2" />
              Transfer na novu lokaciju
            </Button>
          </div>
        </div>
      )}

      {state === "scan-destination" && (
        <div className="flex-1 flex flex-col items-center justify-center p-4 gap-2">
          <p className="text-sm text-muted-foreground">
            {selectedIds.length} lokacija odabrano za premještanje
          </p>
        </div>
      )}
    </div>
  );
}
